/** @format */

import { useEffect, useState } from "react";
import { LuPencil, LuShieldAlert, LuTrash2 } from "react-icons/lu";
import { useForm, FormProvider } from "react-hook-form";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import TqClaimModal from "../components/tqClaimModal";
import TqClaimMapper from "../mappers/tqClaimMapper";
import { handleNext } from "../../../shared/utils/navigationUtils";
import { fetchVehicleClaim } from "../../../shared/services/simpleListItemsService";
import { useNavigate } from "fusion-plugin-react-router";
import {
  saveQuote,
  fetchPanelData,
  fetchPolicyDetails,
} from "../services/quoteService";
import useQuoteStore from "../store/QuoteStore";
import { deformatDateT } from "../../../shared/utils/formatters";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import FieldStack from "../../../shared/components/FieldStack";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import Button from "../../../shared/components/Button";
import useAppStateStore from "../../../shared/store/AppStateStore";

const TqClaim = () => {
  const methods = useForm({ mode: "onChange" });
  const { setValue } = methods;
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [isClaimTypesLoading, setIsClaimTypesLoading] = useState(true);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [showClaimsError, setShowClaimsError] = useState(false);
  const maxClaimsAllowed = 10;

  // Fetch claims from store
  const fetchClaims = () => {
    const storeClaims = TqClaimMapper.get();
    setClaims(storeClaims);
    if (storeClaims.length > 0) setShowClaimsError(false);
  };

  // Get claim type description
  const getClaimDescription = (typeId) => {
    const claimType = claimTypes.find((type) => type.listItemId === typeId);
    return claimType?.listItemDescription || "";
  };

  useEffect(() => {
    fetchClaims();

    let isMounted = true;
    fetchVehicleClaim()
      .then((types) => {
        if (isMounted) setClaimTypes(types);
      })
      .catch((error) => {
        console.error("Error fetching claim types:", error);
      })
      .finally(() => {
        if (isMounted) setIsClaimTypesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDelete = (index) => {
    TqClaimMapper.delete(index);
    fetchClaims();
  };

  const handleClaimEdit = (claim, index) => {
    setClaimModalOpen(true);
    setValue("claimIndex", index);
    setValue("claimType", {
      value: claim.typeId,
      label: getClaimDescription(claim.typeId),
    });
    setValue("claimCost", String(claim.cost ?? ""));
    setValue("WhosFault", claim.atFault ? "Yes" : "No");
    setValue("Injured", claim.injuryInvolved ? "Yes" : "No");
    setValue("AddClaim_Date", deformatDateT(claim.claimDate));
  };

  const handleAddClaim = () => {
    methods.reset({
      claimIndex: null,
      claimType: null,
      claimCost: "",
      Injured: null,
      WhosFault: null,
      AddClaim_Date: null,
    });

    if (claims.length < maxClaimsAllowed) {
      setClaimModalOpen(true);
    }
  };

  useEffect(() => {
    if (claimModalOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [claimModalOpen]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQCLAIM_ENTERED);
  }, []);

  const onClick = () => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQCLAIM_NEXT_CLICKED);
    const latestAdditionalDriver = useAppStateStore.getState().additionalDriver;
    if (latestAdditionalDriver === "Yes") {
      handleNext(QuoteStep.TQ_ADD_DRIVER);
    } else {
      const quoteData = useQuoteStore.getState().quote;
      saveQuote(quoteData, navigate).then((data) => {
        fetchPolicyDetails().catch((err) => {
          console.error("Policy details fetch failed:", err);
        });
        fetchPanelData(navigate, null, data.risk.riskObjectId).then(() => {
          handleNext(QuoteStep.TQ_PANEL);
        });
      });
      handleNext(QuoteStep.TQ_PANEL_LOADER);
    }
  };

  const onInvalidClick = () => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQCLAIM_NEXT_CLICKED);
    setShowClaimsError(true);
  };

  return (
    <FormProvider {...methods}>
      <QuoteFormWrapper>
        <QuoteStepHeading
          title="Claims History"
          description="It's essential to disclose all claims to ensure we provide you with the most accurate quote."
        />

        <FieldStack className="tw-text-sm">
          <label className="tw-mb-2 tw-block tw-text-base tw-font-bold tw-text-dis-navy">
            Have you had any accidents, losses, thefts, fire, flood or claims in
            any vehicle regardless of fault or whether you claimed or not in the
            last 5 years?
          </label>

          <div className="tw-text-sm">
            <div className="tw-flex tw-flex-col">
              {claims.length > 0 &&
                claims.map((claim, index) => (
                  <div
                    className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-dis-divider tw-py-3"
                    key={index}
                  >
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
                      <LuShieldAlert
                        className="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dis-navy"
                        strokeWidth={2.4}
                        aria-hidden="true"
                      />
                      <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
                        {isClaimTypesLoading ? (
                          <span
                            className="tw-h-5 tw-w-32 tw-animate-pulse tw-rounded tw-bg-dis-field"
                            role="status"
                            aria-label="Loading claim type"
                          />
                        ) : (
                          <span className="tw-text-[17px] tw-font-semibold tw-text-dis-navy">
                            {getClaimDescription(claim.typeId)}
                          </span>
                        )}
                        <span className="tw-text-dis-small tw-text-dis-text-muted">
                          {deformatDateT(claim.claimDate)}
                        </span>
                      </div>
                    </div>
                    <div className="tw-flex">
                      <button
                        type="button"
                        onClick={() => handleClaimEdit(claim, index)}
                        className="tw-mr-1.5 tw-flex tw-h-[42px] tw-w-[42px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-dis-field-hover tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                        aria-label={`Edit ${getClaimDescription(
                          claim.typeId,
                        )} claim`}
                      >
                        <LuPencil
                          className="tw-h-5 tw-w-5"
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="tw-flex tw-h-[42px] tw-w-[42px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-dis-field-hover tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                        aria-label={`Delete ${getClaimDescription(
                          claim.typeId,
                        )} claim`}
                      >
                        <LuTrash2
                          className="tw-h-5 tw-w-5"
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                ))}
              {claims.length < maxClaimsAllowed && (
                <div className="tw-mt-5 tw-mb-5 tw-flex">
                  <Button
                    type="button"
                    variant="soft"
                    size="small"
                    onClick={handleAddClaim}
                  >
                    Add a Claim
                  </Button>
                </div>
              )}

              {claims.length == maxClaimsAllowed && (
                <div
                  className="tw-mt-3 tw-border-0 tw-border-l-4 tw-border-solid tw-border-dis-error tw-py-2 tw-pl-3 tw-text-base tw-font-bold tw-text-dis-error"
                  role="alert"
                >
                  You’ve reached the maximum of 10 claims.
                </div>
              )}
            </div>
          </div>
          {showClaimsError && claims.length === 0 && (
            <p
              className="tw-m-0 tw-border-0 tw-border-l-4 tw-border-solid tw-border-l-dis-error tw-bg-dis-error-bg tw-p-3 tw-text-sm tw-text-dis-error"
              role="alert"
            >
              Please add at least one claim before continuing.
            </p>
          )}
        </FieldStack>
      </QuoteFormWrapper>
      <FormNavigationButtons
        type="button"
        prevStep={QuoteStep.TQ6}
        onClick={onClick}
        onInvalidClick={onInvalidClick}
        isValid={claims.length > 0}
      />

      <TqClaimModal
        onClaimSaved={fetchClaims}
        isModalOpen={claimModalOpen}
        setIsModalOpen={setClaimModalOpen}
      />
    </FormProvider>
  );
};

export default TqClaim;
